import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Edit2, Trash2, DollarSign, Clock, CheckCircle, Loader2, X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PackageManagementPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState(null);
  const [editingPackage, setEditingPackage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    duration: '',
    category: '',
    features: ['']
  });

  const [categories, setCategories] = useState([]);

  // Predefined common features for quick selection
  const commonFeatures = [
    'Initial consultation',
    'Site visit',
    'Materials included',
    'Labor included',
    'Cleanup service',
    'Same-day service',
    'Emergency service available',
    '24/7 support',
    '30-day warranty',
    '1-year warranty',
    'Lifetime warranty',
    'Free estimate',
    'Licensed & insured',
    'Background checked',
    'Satisfaction guaranteed',
    'Follow-up service',
    'Premium materials',
    'Eco-friendly options',
    'Next-day service',
    'Weekend availability'
  ];

  useEffect(() => {
    if (user?.userType !== 'professional') {
      navigate('/');
      return;
    }
    fetchPackages();
    fetchCategories();
  }, [user]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchPackages = async () => {
    try {
      // Find provider by user email or name
      const providersRes = await axios.get(`${API_URL}/api/providers`);
      let provider = providersRes.data.find(p => p.email === user?.email);
      
      // Fallback to name if email not found
      if (!provider) {
        provider = providersRes.data.find(p => p.name === user?.name);
      }
      
      if (provider) {
        const packagesRes = await axios.get(`${API_URL}/api/packages?providerId=${provider.id}`);
        setPackages(packagesRes.data);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast({
        title: 'Error',
        description: 'Failed to load packages',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (pkg = null) => {
    if (pkg) {
      setEditingPackage(pkg);
      setFormData({
        name: pkg.name,
        description: pkg.description,
        price: pkg.price.toString(),
        duration: pkg.duration,
        category: pkg.category,
        features: pkg.features
      });
    } else {
      setEditingPackage(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        duration: '',
        category: '',
        features: ['']
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingPackage(null);
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData({ ...formData, features: newFeatures });
  };

  const handleAddFeature = () => {
    setFormData({ ...formData, features: [...formData.features, ''] });
  };

  const handleRemoveFeature = (index) => {
    const newFeatures = formData.features.filter((_, i) => i !== index);
    setFormData({ ...formData, features: newFeatures });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Get provider ID - try by email first, then by name
      const providersRes = await axios.get(`${API_URL}/api/providers`);
      let provider = providersRes.data.find(p => p.email === user?.email);
      
      // If not found by email, try by name (fallback for older providers without email)
      if (!provider) {
        provider = providersRes.data.find(p => p.name === user?.name);
      }
      
      if (!provider) {
        toast({
          title: 'Provider Profile Required',
          description: `No business profile found for ${user?.email}. Please create your business profile first by visiting "Become a Pro" page, or contact support if you already have one.`,
          variant: 'destructive',
          duration: 7000
        });
        setSubmitting(false);
        return;
      }
      
      // Update provider with email if missing
      if (!provider.email && user?.email) {
        try {
          await axios.patch(`${API_URL}/api/providers/${provider.id}`, { email: user.email });
          console.log('Updated provider with email');
        } catch (error) {
          console.error('Failed to update provider email:', error);
        }
      }

      const filteredFeatures = formData.features.filter(f => f.trim() !== '');
      
      if (filteredFeatures.length === 0) {
        toast({
          title: 'Features Required',
          description: 'Please select or add at least one feature for your package.',
          variant: 'destructive'
        });
        setSubmitting(false);
        return;
      }

      const packageData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        duration: formData.duration,
        category: formData.category,
        features: filteredFeatures,
        providerId: provider.id
      };

      console.log('Submitting package data:', packageData);

      if (editingPackage) {
        // Update existing package
        await axios.patch(`${API_URL}/api/packages/${editingPackage.id}`, packageData);
        toast({
          title: 'Success!',
          description: 'Package updated successfully'
        });
      } else {
        // Create new package
        await axios.post(`${API_URL}/api/packages`, packageData);
        toast({
          title: 'Success!',
          description: 'Package created successfully'
        });
      }

      fetchPackages();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving package:', error);
      console.error('Error details:', error.response);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || error.message || 'Failed to save package',
        variant: 'destructive',
        duration: 5000
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (pkg) => {
    console.log('Delete button clicked for package:', pkg.id);
    setPackageToDelete(pkg);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!packageToDelete) return;
    
    console.log('Deleting package:', packageToDelete.id);
    setDeleting(true);
    
    try {
      const response = await axios.delete(`${API_URL}/api/packages/${packageToDelete.id}`);
      console.log('Delete response:', response.data);
      
      toast({
        title: 'Success!',
        description: 'Package deleted successfully'
      });
      
      setDeleteModalOpen(false);
      setPackageToDelete(null);
      fetchPackages();
    } catch (error) {
      console.error('Error deleting package:', error);
      console.error('Error response:', error.response);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || error.message || 'Failed to delete package',
        variant: 'destructive',
        duration: 5000
      });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Service Packages</h1>
            <p className="text-gray-600 mt-2">Create and manage your service offerings</p>
          </div>
          <Button
            onClick={() => handleOpenModal()}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Package
          </Button>
        </div>

        {/* Package Grid */}
        {packages.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No packages yet</h3>
              <p className="text-gray-600 mb-4">Create your first service package to start accepting bookings</p>
              <Button
                onClick={() => handleOpenModal()}
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Package
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {packages.map((pkg) => (
              <Card key={pkg.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl">{pkg.name}</CardTitle>
                      <Badge className="mt-2" variant="secondary">{pkg.category}</Badge>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenModal(pkg)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteClick(pkg)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm mb-4">{pkg.description}</p>
                  
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center text-gray-700">
                      <DollarSign className="h-4 w-4 text-orange-600 mr-2" />
                      <span className="font-semibold">${pkg.price} CAD</span>
                      <span className="text-xs text-gray-500 ml-2">(+ $10 platform fee)</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <Clock className="h-4 w-4 text-orange-600 mr-2" />
                      <span>{pkg.duration}</span>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-2">Includes:</p>
                    <ul className="space-y-1">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-600">
                          <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Package Modal */}
      <Dialog open={modalOpen} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPackage ? 'Edit Package' : 'Create New Package'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Package Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Basic Plumbing Service"
                required
              />
            </div>

            <div>
              <Label>Category *</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.name} value={cat.name}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe what's included in this package..."
                rows={3}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Price (CAD) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="100.00"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">10% platform fee will be added at checkout</p>
              </div>

              <div>
                <Label>Duration *</Label>
                <Input
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  placeholder="e.g., 2 hours, Half day"
                  required
                />
              </div>
            </div>

            <div>
              <Label>Features Included *</Label>
              <p className="text-sm text-gray-600 mb-3">Select common features or add custom ones</p>
              
              {/* Quick Select Common Features */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4 max-h-64 overflow-y-auto">
                <p className="text-sm font-medium text-gray-700 mb-2">Quick Select:</p>
                <div className="grid grid-cols-2 gap-2">
                  {commonFeatures.map((feature) => (
                    <div key={feature} className="flex items-center space-x-2">
                      <Checkbox
                        id={feature}
                        checked={formData.features.includes(feature)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setFormData({ 
                              ...formData, 
                              features: [...formData.features.filter(f => f !== ''), feature]
                            });
                          } else {
                            setFormData({ 
                              ...formData, 
                              features: formData.features.filter(f => f !== feature)
                            });
                          }
                        }}
                      />
                      <label
                        htmlFor={feature}
                        className="text-sm text-gray-700 cursor-pointer leading-tight"
                      >
                        {feature}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Selected Features Display */}
              {formData.features.filter(f => f !== '').length > 0 && (
                <div className="mb-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">Selected Features:</p>
                  <div className="flex flex-wrap gap-2">
                    {formData.features.filter(f => f !== '').map((feature, index) => (
                      <Badge key={index} variant="secondary" className="flex items-center gap-1">
                        {feature}
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              features: formData.features.filter((_, i) => i !== index)
                            });
                          }}
                          className="ml-1 hover:text-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom Feature Input */}
              <div>
                <Label className="text-sm">Add Custom Feature</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    placeholder="Type a custom feature..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        e.preventDefault();
                        setFormData({ 
                          ...formData, 
                          features: [...formData.features.filter(f => f !== ''), e.target.value.trim()]
                        });
                        e.target.value = '';
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      const input = e.target.closest('div').querySelector('input');
                      if (input.value.trim()) {
                        setFormData({ 
                          ...formData, 
                          features: [...formData.features.filter(f => f !== ''), input.value.trim()]
                        });
                        input.value = '';
                      }
                    }}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Press Enter or click + to add</p>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={handleCloseModal}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingPackage ? 'Update Package' : 'Create Package'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Package</DialogTitle>
          </DialogHeader>
          
          {packageToDelete && (
            <div className="space-y-4">
              <p className="text-gray-700">
                Are you sure you want to delete this package? This action cannot be undone.
              </p>
              
              <div className="bg-gray-50 rounded-md p-3">
                <p className="font-medium text-gray-900">{packageToDelete.name}</p>
                <p className="text-sm text-gray-600 mt-1">${packageToDelete.price} CAD - {packageToDelete.duration}</p>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => {
                    setDeleteModalOpen(false);
                    setPackageToDelete(null);
                  }}
                  variant="outline"
                  className="flex-1"
                  disabled={deleting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteConfirm}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Package
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
      <Toaster />
    </div>
  );
};

export default PackageManagementPage;
